<template>
  <div class="payment-method-select">
    <option-label
      id="payment-method-heading"
      :title="t('donationDetails.form.paymentMethod.label')"
      class="mb-2"
    />
    <v-radio-group
      v-model="selectedMethodId"
      data-cy="payment-method-select"
      :aria-labelledby="'payment-method-heading'"
      :disabled="loading"
      :rules="[rules.requiredSelection(t('confirm.form.paymentMethod.label'))]"
      class="payment-method-select__group"
      color="#047481"
      hide-details="auto"
    >
      <v-radio
        v-for="method in effectivePaymentMethods"
        :key="method.id"
        :value="method.id"
        :label="method.title"
        :data-cy="`payment-method-radio-${method.type}`"
        class="payment-method-select__option"
      />
    </v-radio-group>
    <v-divider v-if="!hideDivider" class="mt-4 mb-4" />
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { PaymentMethod } from '@/classes/PaymentMethod';
import { useLabel } from '@/composables/useLabel';
import { useValidationRules } from '@/composables/useValidationRules';
import OptionLabel from '@/components/OptionLabel.vue';
import matomo from '@/services/matomoService';

const componentProps = defineProps({
  modelValue: { type: Object as PropType<PaymentMethod>, default: null },
  paymentMethods: { type: Array as PropType<PaymentMethod[]>, default: () => [] },
  hideDivider: { type: Boolean, default: false },
  loading: Boolean,
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useLabel();
const { rules } = useValidationRules(t);

const effectivePaymentMethods = computed(() => {
  const methods = componentProps.paymentMethods || [];
  const seenIds = new Set<number>();
  // Deduplicate by id so duplicate session payloads (e.g. from external agents) don't show twice
  return methods
    .filter((method: PaymentMethod) => {
      if (method?.id == null || seenIds.has(method.id)) return false;
      seenIds.add(method.id);
      return true;
    })
    .map((method: PaymentMethod) => {
      const translated = t(method.nameTranslationKey);
      const title = typeof translated === 'string' ? translated : method.name || method.type || '';
      return {
        ...method,
        title,
      } as PaymentMethod & { title: string };
    });
});

const selectedMethodId = computed({
  get(): number | null {
    return componentProps.modelValue?.id ?? null;
  },
  set(id: number | null) {
    const method = (componentProps.paymentMethods || []).find(
      (paymentMethod: PaymentMethod) => paymentMethod.id === id
    );
    emit('update:modelValue', method || null);
    emit('change');
    setTimeout(() => {
      handlePaymentMethodTracking();
    }, 0);
  },
});

const handlePaymentMethodTracking = () => {
  matomo.trackEvent(
    'checkout',
    'payment_method_selected',
    'donations_checkout_payment_method_selected'
  );
};
</script>

<style scoped lang="scss">
.payment-method-select {
  margin-bottom: 8px;

  &__group {
    margin-top: 0;
    padding-top: 0;

    :deep(.v-input__control) {
      background-color: transparent !important;
      border: none !important;
      border-radius: 0 !important;
      display: block;
    }

    :deep(.v-selection-control-group) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    :deep(.v-selection-control),
    :deep(.v-selection-control--density-default) {
      flex-direction: row !important;
      justify-content: flex-start !important;
      align-items: center;
      background-color: transparent !important;
      border: none !important;
      min-height: 24px;
      height: auto;
      width: auto;
      max-width: 100%;
      margin: 0 !important;
      padding: 0 !important;
      column-gap: 12px;
    }

    :deep(.v-label) {
      font-family: 'Lato', sans-serif;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      color: #333c4e;
      opacity: 1;
    }

    :deep(.v-selection-control__wrapper) {
      width: 20px;
      height: 20px;
    }

    :deep(.v-selection-control__input) {
      width: 20px;
      height: 20px;
    }

    :deep(.v-selection-control__input > .v-icon) {
      font-size: 20px;
      opacity: 1;
    }

    :deep(.v-selection-control:not(.v-selection-control--dirty) .v-selection-control__input > .v-icon) {
      color: #d1d5db;
    }

    :deep(.v-selection-control--dirty .v-selection-control__input > .v-icon) {
      color: #047481;
    }
  }
}
</style>
